///
/// EarthBrowser
///
/// Created by Matt Giger on 11/24/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type Database } from '@/earthbrowser/database/Database'
import { useCallback, useEffect, useState } from 'react'

interface LockRequest {
	requesterId: string
	timestamp: number
}

export function useDatabaseLockCoordination(database: Database | null) {
	const [lockRequest, setLockRequest] = useState<LockRequest | null>(null)
	const [hasLock, setHasLock] = useState(true)

	useEffect(() => {
		if (!database) {
			return
		}

		const handleLockRequested = (requesterId: string) => {
			setLockRequest({
				requesterId,
				timestamp: Date.now()
			})
		}

		const handleLockLost = () => {
			setHasLock(false)
			setLockRequest(null)
		}

		const handleLockAcquired = () => {
			setHasLock(true)
			setLockRequest(null)
		}

		database.setOnLockRequested(handleLockRequested)
		database.setOnLockLost(handleLockLost)
		database.setOnLockAcquired(handleLockAcquired)

		return () => {
			database.setOnLockRequested(() => {})
			database.setOnLockLost(() => {})
			database.setOnLockAcquired(() => {})
		}
	}, [database])

	// Listen for visibility and focus changes to request access back
	useEffect(() => {
		if (!database || hasLock) {
			return
		}

		const handleActivation = () => {
			// Check if tab is visible AND window is focused
			const isActive = !document.hidden && document.hasFocus()
			if (isActive && !hasLock) {
				// Tab/window became active and we don't have the lock - request it back
				void database.requestAccess()
					.then(() => {
						setHasLock(true)
					})
					.catch((error) => {
						console.error('Failed to regain database access:', error)
					})
			}
		}

		// Listen for both tab visibility and window focus changes
		document.addEventListener('visibilitychange', handleActivation)
		window.addEventListener('focus', handleActivation)

		return () => {
			document.removeEventListener('visibilitychange', handleActivation)
			window.removeEventListener('focus', handleActivation)
		}
	}, [database, hasLock])

	const releaseLock = useCallback(() => {
		if (database) {
			// Destroy the database, which will close the VFS and release the lock
			void database.destroy().then(() => {
				// Reload the page to allow the other tab to acquire the lock
				window.location.reload()
			})
		}
	}, [database])

	const dismissRequest = useCallback(() => {
		setLockRequest(null)
	}, [])

	return {
		lockRequest,
		hasLock,
		releaseLock,
		dismissRequest
	}
}

